import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import {
  useDashboard,
  useApiQuery,
  useInfiniteApiQuery,
  useTeam,
} from '@vrplatform/data';
import { hasValue, type Maybe } from '@vrplatform/utils';
import { useMemo, useState } from 'react';
import type { BaseCustomFilterProps } from './_types';
import {
  getListingsFilterQuery,
  mapListingToSelectItem,
} from './ListingsFilter.utils';
import { Icon } from '@vrplatform/icons';

interface Props extends BaseCustomFilterProps {
  listingIds: Maybe<string[]>;
  setListingIds: (value: string[] | null) => void;
  ownerIds?: Maybe<string[]>;
  onUnmappedSelected?: (v: 'unmapped') => void;
}

const UNMAPPED_OPTION: SelectItem = {
  label: 'Unmapped',
  value: 'unmapped',
  icon: <Icon icon="MinusCircleIcon" size={16} />,
};

export const ListingsFilter = ({
  listingIds: initial,
  setListingIds,
  label = 'Listing',
  ownerIds,
  onUnmappedSelected,
}: Props) => {
  const [dashboard] = useDashboard();
  const [{ id: teamId }] = useTeam();

  const listingIds = initial || [];
  const [search, setSearch] = useState('');
  const gate = useSelectQueryGate({ value: listingIds });
  const shouldPrefetchOptions = dashboard === 'owner' && !search.trim();
  const shouldFetchOptions = gate.shouldFetchOptions || shouldPrefetchOptions;

  const queryData = useInfiniteApiQuery(
    async (api, signal, variables, pageParam) => {
      const response = await api.GET('/listings', {
        params: {
          query: getListingsFilterQuery({
            page: pageParam,
            search: variables.search,
            ownerIds: variables.ownerIds,
          }),
        },
        signal,
      });

      if (response.error) {
        throw response.error;
      }

      return {
        list: response.data.data.map(mapListingToSelectItem),
        aggregate: response.data.pagination.total,
        pagination: response.data.pagination,
      };
    },
    {
      skip: !teamId || !shouldFetchOptions,
      queryGroup: 'listings',
      uniqueQueryKey: 'listings-filter',
      queryKeys: ['listings'],
      variables: {
        teamId,
        search: search.trim(),
        ownerIds,
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.pagination.nextPage ?? null,
    }
  );

  const { data } = useApiQuery(
    async (api, signal, variables) => {
      if (variables.listingIds.includes(UNMAPPED_OPTION.value)) {
        return { value: [UNMAPPED_OPTION] };
      }

      if (!variables.listingIds.length) {
        return { value: [] as SelectItem[] };
      }

      const listings = await Promise.all(
        variables.listingIds.map(async (id) => {
          const response = await api.GET('/listings/{id}', {
            params: {
              path: {
                id,
              },
            },
            signal,
          });

          if (response.error) {
            throw response.error;
          }

          return response.data;
        })
      );

      return {
        value: listings.map(mapListingToSelectItem),
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryGroup: 'listings',
      uniqueQueryKey: 'listings-filter-value',
      queryKeys: ['listings'],
      keepPreviousData: true,
      variables: {
        teamId,
        listingIds: (listingIds || []).filter(hasValue),
      },
    }
  );

  const value = gate.hasValue ? data?.value || [] : [];

  const bottomAction = useMemo(
    () =>
      onUnmappedSelected
        ? [
            {
              id: UNMAPPED_OPTION.value,
              label: UNMAPPED_OPTION.label,
              onSubmit: () => {
                onUnmappedSelected('unmapped');
              },
              icon: UNMAPPED_OPTION.icon,
            },
          ]
        : undefined,
    [onUnmappedSelected]
  );

  if (dashboard === 'owner' && !search.trim() && shouldPrefetchOptions) {
    if (queryData.isLoading) return <LoadingIndicator size="18" />;
    if ((queryData.data?.pages[0]?.list.length ?? 0) < 2) return null;
  }

  return (
    <Filter.Select
      value={value}
      setValue={(next) =>
        setListingIds(next.length ? next.map((entry) => entry.value) : null)
      }
      label={label}
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
      customBottomActions={bottomAction}
    />
  );
};
